import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import useInitPurchase from '@hooks/useInitPurchase';
import MetaData from '@/bit/core/core.meta-data';
import { wrapper } from 'store';
import FormLayout from '@/containers/formLayout';
import Title, { TITLE_SIZE } from '@/components/title';
import ClientAddress from '@/containers/clientAddress';
import ClientPersonalData from '@/containers/clientPersonalData';
import { SEGMENT, BUBBLE_TEXTS, DEVICE_KIND } from '@/constants/constant';
import {
  URL_SEO,
  PATH_NAME,
  FORM_ID,
  CLIENT_ORION_TEXTS_IDS,
} from '@/constants/pages/additionalFiber';
import { PERSONAL_DATA } from '@/constants/personalData';
import FormBafClientFake from '@/components/fakes/formPagesFake/formBafClientFake';
import { capitalizeWords } from '@/utils/common';
import { getStaticProps as getStaticPropsMethod } from '@/bit/pages/pages.create-pages';
import getDeviceKindDependsOnViewPort from '@gcloud-npm/utils.common.get-device-kind-depends-on-viewport';
import { actions } from '@/reducers/form-additional-fiber-reducer';
import { actions as retargetingActions } from '@/reducers/retargeting-reducer';
import { INSTALLATION_ADDRESS } from '@/constants/installationAddress';
import { actions as modalActions } from '@/bit/reducers/reducers.modals-reducer';
import {
  OPACITY_LAYOUT_COLORS,
  CONTENT_POSITION,
  IN_SCENE_EFFECT,
  OUT_SCENE_EFFECT,
} from '@/bit/components/components.modal';
import { getOrionInfoTextByAlias } from '@/sagas/additionalFiberForm/utils/formDataUtils';

const AdditionalFiber = ({ seoInfo }) => {
  const dispatch = useDispatch();
  const formAdditionalFiber = useSelector(
    (state) => state.formAdditionalFiber
  );

  const { loadingButtonShoppingCart, loadingShoppingCart, shoppingCart, customerInfo, pageTexts } = formAdditionalFiber;
  const [showErrorBubble, setShowErrorBubble] = useState(false);
  const {
    unregister,
    getValues,
    handleSubmit,
    resetField,
    control,
    setValue,
    trigger,
  } = useForm({
    mode: 'onTouched',
  });

  const onSubmit = async (formData) => {
    setShowErrorBubble(false);
    const deviceKind = getDeviceKindDependsOnViewPort();
    dispatch(
      actions.checkout({
        formData,
        deviceKind,
        loadingButtonShoppingCart: true,
      })
    );
  };
  const onError = (formErrors) => {
    if (Object.keys(formErrors).length > 1) {
      setShowErrorBubble(true);
      const scrollElement = document.getElementById(FORM_ID.GP);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    } else if (Object.keys(formErrors).length === 1) {
      setShowErrorBubble(false);
      const scrollElement = document.getElementById(Object.keys(formErrors)[0]);
      if (scrollElement) {
        scrollElement.scrollIntoView({ behavior: 'instant' });
      }
    }
  };
  const { setSegment, setFirstZone, initPurchase } = actions;
  useInitPurchase({
    segment: SEGMENT.particulares,
    initPurchase,
    setSegment,
    setZone: setFirstZone,
    loaded: !!shoppingCart,
  });
  useEffect(() => {
    if (
      shoppingCart !== null &&
      window.grecaptcha &&
      window.grecaptcha.enterprise
    ) {
      window.grecaptcha.enterprise.ready(() => {
        dispatch(
          retargetingActions.sendRetargeting({
            ...shoppingCart,
            campaignId: 'ret-fa-cart',
          })
        );
      });
    }
  }, [shoppingCart, dispatch]);

  const handleValidateEditContactPhone = (phone) => {
    if (phone) {
      setValue(PERSONAL_DATA.MODAL_CONTACT_PHONE.ID, phone);
    }
  };
  const handleEditContactPhone = () => {
    const deviceKind = getDeviceKindDependsOnViewPort();
    const ModalLegalConditions = dynamic(() =>
      import('@/modals/changeContactPhoneModal')
    );
    dispatch(
      modalActions.addModal({
        createAnchor: false,
        type: 'MODAL',
        id: 'adviseChangeZone',
        Element2Show: ModalLegalConditions,
        elementProps: {
          validate: handleValidateEditContactPhone,
          fieldName: PERSONAL_DATA.MODAL_CONTACT_PHONE,
          control,
          trigger,
          getValues,
          unregister,
        },
        modalProps: {
          className: '',
          contentPosition:
            deviceKind === DEVICE_KIND.MOBILE
              ? CONTENT_POSITION.CENTER_BOTTOM
              : CONTENT_POSITION.CENTER_CENTER,
          opacityLayoutColor: OPACITY_LAYOUT_COLORS.BLACK05,
          inSceneEffect:
            deviceKind === DEVICE_KIND.MOBILE
              ? IN_SCENE_EFFECT.FADE_IN_BOTTOM
              : IN_SCENE_EFFECT.FADE_IN,
          outSceneEffect:
            deviceKind === DEVICE_KIND.MOBILE
              ? OUT_SCENE_EFFECT.FADE_OUT_BOTTOM
              : OUT_SCENE_EFFECT.FADE_OUT,
          closeByClickingOutside: true,
          contentInsideContainer: deviceKind !== DEVICE_KIND.MOBILE,
        },
      })
    );
  };

  return (
    <>
      <Head>
        <script
          async
          src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_LAYER_RECAPTCHA_ID}`}
        />
      </Head>
      <MetaData seoInfo={seoInfo} />
      <FormLayout
        onSubmit={handleSubmit(onSubmit, onError)}
        control={control}
        showErrorBubble={showErrorBubble}
        textBubble={BUBBLE_TEXTS.BUBBLE_ERROR_TEXT}
        loaded={!!shoppingCart}
        shoppingCart={shoppingCart}
        loadingShoppingCart={loadingShoppingCart}
        loadingButtonShoppingCart={loadingButtonShoppingCart}
        nextStepsText={getOrionInfoTextByAlias(CLIENT_ORION_TEXTS_IDS.NEXT_STEPS, pageTexts)?.subTitle}
        formFake={<FormBafClientFake />}
      >
        <Title
          text={`Hola${
            customerInfo?.name ? ` ${capitalizeWords(customerInfo?.name)}` : ''
          }, completa tu pedido`}
          size={TITLE_SIZE.TITLE5}
        />
        <ClientAddress
          unregister={unregister}
          control={control}
          getValues={getValues}
          resetField={resetField}
          address={customerInfo?.installationAddress}
          addressFormStructure={INSTALLATION_ADDRESS}
          addressAction={actions.setInfo}
          editable={false}
          setValue={setValue}
        />
        <ClientPersonalData
          title="Datos de contacto y pago"
          contactEmail={customerInfo?.contactEmail}
          contactPhone={customerInfo?.contactPhone}
          payment={customerInfo?.payment}
          installationContactPhone={getValues(
            PERSONAL_DATA.MODAL_CONTACT_PHONE.ID
          )}
          edit={
            customerInfo?.installationContactPhone
              ? handleEditContactPhone
              : null
          }
          control={control}
          setValue={setValue}
        />
      </FormLayout>
    </>
  );
};
export default AdditionalFiber;

export const getStaticProps = wrapper.getStaticProps(async (data) => {
  return getStaticPropsMethod(data, '', 2, {
    pathname: PATH_NAME.GP,
    orionAlias: '',
    seoLocation: URL_SEO,
    headerLocation: '/',
    layoutInfo: '',
  });
});
